import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { css } from '../lib/css'
import { Hover } from '../lib/Hover'
import { useGo, paths } from '../lib/nav'
import { useT, barePath, langFromPath } from '../i18n'

// Languages offered in the selector. Add a new one here (plus its /es-style route
// prefix and dictionary) and the dropdown scales automatically.
const LANGS: ['en' | 'es', string][] = [
  ['en', 'English'],
  ['es', 'Español'],
]

const mobileItem = `padding: 13px 2px; font-size: 15.5px; font-weight: 500; color: #e8f4f6; border-bottom: 1px solid rgba(127,212,196,.12); cursor: pointer`

export default function Header() {
  const go = useGo()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const t = useT()
  const lang = langFromPath(pathname)
  const [menuOpen, setMenuOpen] = useState(false)
  const [progOpen, setProgOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const programsActive = ([paths.programs, paths.youngAdults, paths.adults, paths.keys] as string[]).includes(barePath(pathname))

  // Close the language dropdown on any outside click.
  useEffect(() => {
    if (!langOpen) return
    const close = () => setLangOpen(false)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [langOpen])

  // Language selector: a compact dropdown. The toggle shows the current language
  // code (EN / ES); the menu lists full names and jumps to the same page in that
  // language. Scales to more languages via LANGS, no layout change.
  const toLangPath = (code: 'en' | 'es') => {
    const bare = barePath(pathname)
    return code === 'en' ? bare : bare === '/' ? '/es' : '/es' + bare
  }
  const chooseLang = (code: 'en' | 'es') => {
    setLangOpen(false)
    if (code !== lang) navigate(toLangPath(code))
  }
  const langSwitch = (
    <div style={css(`position: relative; flex: none`)}>
      <div
        onClick={(e) => { e.stopPropagation(); setLangOpen((o) => !o) }}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={langOpen}
        aria-label={t('Language')}
        style={css(`display: inline-flex; align-items: center; gap: 5px; padding: 6px 11px; border: 1px solid rgba(127,212,196,.45); border-radius: 999px; cursor: pointer; font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: 700; letter-spacing: .04em; color: #f2fbfa; white-space: nowrap`)}
      >
        {lang.toUpperCase()}
        <span style={css(`font-size: 8px; opacity: .85`)}>&#9660;</span>
      </div>
      {langOpen && (
        <div role="listbox" style={css(`position: absolute; top: calc(100% + 8px); right: 0; background: #16233c; border: 1px solid rgba(127,212,196,.3); border-radius: 10px; overflow: hidden; min-width: 136px; box-shadow: 0 18px 44px -20px rgba(0,0,0,.65); z-index: 60`)}>
          {LANGS.map(([code, label]) => (
            <div key={code} role="option" aria-selected={code === lang} onClick={() => chooseLang(code)} style={css(`padding: 11px 15px; font-size: 14px; cursor: pointer; white-space: nowrap; color: ${code === lang ? '#7fd4c4' : '#e8f4f6'}; background: ${code === lang ? 'rgba(127,212,196,.1)' : 'transparent'}`)}>{label}</div>
          ))}
        </div>
      )}
    </div>
  )

  // Top-nav items; the current route renders in brand orange (no underline).
  const NAV: [string, string, () => void][] = [
    ['About', paths.about, go.goAbout],
    ['Who we serve', paths.serve, go.goServe],
    ['Housing model', paths.model, go.goModel],
    ['Outcomes', paths.outcomes, go.goOutcomes],
    ['Get involved', paths.involved, go.goInvolved],
    ['Partner', paths.partner, go.goPartner],
    ['Contact', paths.contact, go.goContact],
  ]
  // Navigating always closes the mobile menu (mirrors the original `go()`).
  const nav = (fn: () => void) => () => {
    setMenuOpen(false)
    setProgOpen(false)
    fn()
  }

  const navSpan = ([label, path, handler]: [string, string, () => void]) => {
    const active = barePath(pathname) === path
    return (
      <Hover
        key={path}
        as="span"
        onClick={nav(handler)}
        style={css(`cursor: pointer; padding: 4px 0; color: ${active ? '#ef7a4f' : '#ffffff'}`)}
        hoverStyle={css(active ? `color: #ff8c60` : `color: #7fd4c4`)}
      >
        {t(label)}
      </Hover>
    )
  }

  const PROGRAMS: [string, () => void, string][] = [
    ['Young Adults (18–24)', go.goYoungAdults, paths.youngAdults],
    ['Adults (25+)', go.goAdults, paths.adults],
    ['KEYS', go.goKeys, paths.keys],
  ]

  return (
    <div
      style={css(
        `position: sticky; top: 0; z-index: 40; background: #0f1a2e; border-bottom: 1px solid rgba(127,212,196,.22); color: #f2fbfa`,
      )}
    >
      <div
        data-mq-header="1"
        style={css(
          `padding: 0 clamp(20px, calc((100% - 1240px) / 2), 480px); height: 74px; display: flex; align-items: center; justify-content: space-between; gap: 16px`,
        )}
      >
        <div onClick={nav(go.goHome)} style={css(`display: flex; align-items: center; cursor: pointer; flex: none`)}>
          <img
            src="/assets/img/baf19bfe-81c6-4c6f-b355-d4e0b8766295.png"
            alt="Maya House"
            style={css(`height: 54px; width: auto; display: block; filter: drop-shadow(0 0 18px rgba(255,255,255,.55))`)}
          />
        </div>

        <div
          data-mq-hide="1"
          style={css(
            `display: flex; gap: 15px; font-size: 13.5px; font-weight: 500; color: #ffffff; min-width: 0; flex-wrap: nowrap; white-space: nowrap`,
          )}
        >
          {navSpan(['Home', paths.home, go.goHome])}
          {navSpan(NAV[0])}
          {navSpan(NAV[1])}
          <div
            onMouseEnter={() => setProgOpen(true)}
            onMouseLeave={() => setProgOpen(false)}
            style={css(`position: relative; display: flex; align-items: center`)}
          >
            <Hover
              as="span"
              onClick={nav(go.goPrograms)}
              style={css(`cursor: pointer; padding: 4px 0; color: ${programsActive ? '#ef7a4f' : '#ffffff'}; display: inline-flex; align-items: center; gap: 5px`)}
              hoverStyle={css(programsActive ? `color: #ff8c60` : `color: #7fd4c4`)}
            >
              {t('Programs')}
              <span style={css(`font-size: 9px; opacity: .85`)}>&#9662;</span>
            </Hover>
            {progOpen && (
              <div style={css(`position: absolute; top: 100%; left: 50%; transform: translateX(-50%); padding-top: 8px; z-index: 50`)}>
                <div style={css(`background: #0f1a2e; border: 1px solid rgba(127,212,196,.22); border-radius: 10px; padding: 8px; display: flex; flex-direction: column; min-width: 200px; box-shadow: 0 16px 40px rgba(0,0,0,.4)`)}>
                  {PROGRAMS.map(([label, handler, path]) => {
                    const active = barePath(pathname) === path
                    return (
                      <Hover
                        key={path}
                        as="span"
                        onClick={nav(handler)}
                        style={css(`cursor: pointer; padding: 9px 12px; border-radius: 7px; font-size: 14px; color: ${active ? '#7fd4c4' : '#e8f4f6'}; white-space: nowrap`)}
                        hoverStyle={css(`background: rgba(127,212,196,.12); color: #7fd4c4`)}
                      >
                        {t(label)}
                      </Hover>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
          {navSpan(NAV[2])}
          {navSpan(NAV[3])}
          {navSpan(NAV[4])}
          {navSpan(NAV[5])}
          {navSpan(NAV[6])}
        </div>

        <div style={css(`display: flex; gap: 12px; align-items: center; flex: none`)}>
          {langSwitch}
          <Hover
            data-mq-hide="1"
            onClick={nav(go.goRefer)}
            style={css(
              `font-size: 13px; font-weight: 600; padding: 11px 15px; border: 1px solid rgba(127,212,196,.45); border-radius: 999px; color: #f2fbfa; cursor: pointer; white-space: nowrap`,
            )}
            hoverStyle={css(`background: rgba(127,212,196,.14); border-color: #7fd4c4`)}
          >
            {t('Make a referral')}
          </Hover>
          <Hover
            onClick={nav(go.goGive)}
            style={css(
              `font-size: 13px; font-weight: 700; padding: 11px 16px; background: #fff; color: #16233c; border-radius: 999px; cursor: pointer; white-space: nowrap`,
            )}
            hoverStyle={css(`background: #dbe9ee`)}
          >
            {t('Donate now')}
          </Hover>
          <div
            data-mq-menu="1"
            onClick={() => setMenuOpen((v) => !v)}
            role="button"
            tabIndex={0}
            aria-label="Menu"
            aria-expanded={menuOpen}
            style={css(`display: none; flex-direction: column; gap: 5px; justify-content: center; padding: 12px 6px; cursor: pointer`)}
          >
            <span style={css(`display: block; width: 22px; height: 2px; background: #f2fbfa`)}></span>
            <span style={css(`display: block; width: 22px; height: 2px; background: #f2fbfa`)}></span>
            <span style={css(`display: block; width: 22px; height: 2px; background: #f2fbfa`)}></span>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div
          style={css(
            `display: flex; flex-direction: column; padding: 4px 20px 18px; border-top: 1px solid rgba(127,212,196,.18); background: rgba(15,26,46,.98); max-height: calc(100svh - 62px); overflow: auto`,
          )}
        >
          <span onClick={nav(go.goHome)} style={css(mobileItem)}>{t('Home')}</span>
          <span onClick={nav(go.goAbout)} style={css(mobileItem)}>{t('About us')}</span>
          <span onClick={nav(go.goServe)} style={css(mobileItem)}>{t('Who we serve')}</span>
          <span onClick={nav(go.goPrograms)} style={css(`${mobileItem}; font-weight: 600`)}>{t('Programs')}</span>
          <span onClick={nav(go.goYoungAdults)} style={css(`${mobileItem}; padding-left: 14px`)}>{t('Young Adults (18–24)')}</span>
          <span onClick={nav(go.goAdults)} style={css(`${mobileItem}; padding-left: 14px`)}>{t('Adults (25+)')}</span>
          <span onClick={nav(go.goKeys)} style={css(`${mobileItem}; padding-left: 14px`)}>{t('KEYS')}</span>
          <span onClick={nav(go.goModel)} style={css(mobileItem)}>{t('Our housing model')}</span>
          <span onClick={nav(go.goOutcomes)} style={css(mobileItem)}>{t('Outcomes')}</span>
          <span onClick={nav(go.goInvolved)} style={css(mobileItem)}>{t('Get involved')}</span>
          <span onClick={nav(go.goPartner)} style={css(mobileItem)}>{t('Partner with us')}</span>
          <span onClick={nav(go.goContact)} style={css(`padding: 13px 2px; font-size: 15.5px; font-weight: 500; color: #e8f4f6; border-bottom: 1px solid rgba(127,212,196,.12); cursor: pointer`)}>{t('Contact')}</span>
          <Hover
            onClick={nav(go.goGive)}
            style={css(`margin-top: 14px; text-align: center; font-size: 15px; font-weight: 700; padding: 15px; background: #fff; color: #16233c; border-radius: 999px; cursor: pointer`)}
            hoverStyle={css(`background: #dbe9ee`)}
          >
            {t('Donate now')}
          </Hover>
          <div
            onClick={nav(go.goRefer)}
            style={css(`margin-top: 10px; text-align: center; font-size: 14px; font-weight: 600; padding: 13px; border: 1px solid rgba(127,212,196,.45); border-radius: 999px; color: #f2fbfa; cursor: pointer`)}
          >
            {t('Make a referral')}
          </div>
        </div>
      )}
    </div>
  )
}
